/** /translation-glossary — per-operator translation glossaries (backend metaplan A6, AIE §8).
 *
 *  Every machine translation of an operator — auto-translated tag values (translation
 *  bindings) and the AI editor's translations — applies the operator's glossary: `keep`
 *  terms stay verbatim, `translate` terms come out as the operator's preferred translation.
 *  The grid follows the console tenant scope; a term belongs to exactly one operator.
 *  Gated by `console_translation_glossary_view` / `_manage` (Ops Admin by default). */
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, Chip, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import * as glossaryApi from '@/api/translationGlossary';
import type { GlossaryKind, GlossaryTerm } from '@/api/translationGlossary';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { ResourceDataGrid, type ResourceDataGridParams } from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds } from '@/scope/tenantIds';
import { useScopedTenantsState } from '@/scope/useScopedTenantsState';
import { formatRelativeTime } from '@/utils/format';

import { GlossaryTermDialog } from './GlossaryTermDialog';

const KIND_LABEL: Record<GlossaryKind, string> = { keep: 'keep verbatim', translate: 'preferred translation' };

/** `it: punto d'incontro · fr: point de rencontre` */
export function translationsSummary(term: Pick<GlossaryTerm, 'kind' | 'translations'>): string {
  if (term.kind === 'keep') return '—';
  return Object.entries(term.translations)
    .map(([lang, text]) => `${lang}: ${text}`)
    .join(' · ');
}

export function TranslationGlossaryPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_translation_glossary_manage');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  // The operators (several, D1); they follow the console scope until picked here.
  const [tenants, setTenants] = useScopedTenantsState();
  const [kindFilter, setKindFilter] = useState<'any' | GlossaryKind>('any');
  const tenantIds = formatTenantIds(tenants.map((t) => t.id));

  const anyFilterActive = tenants.length > 0 || kindFilter !== 'any';
  const resetAllFilters = () => {
    setTenants([]);
    setKindFilter('any');
  };

  const extra = useMemo(() => {
    const e: Record<string, string | number | boolean> = {};
    if (tenantIds) e.tour_operator_id = tenantIds;
    if (kindFilter !== 'any') e.kind = kindFilter;
    return e;
  }, [tenantIds, kindFilter]);

  const editQuery = useQuery({
    queryKey: ['translation_glossary', 'detail', editId],
    queryFn: () => glossaryApi.retrieve(editId as number),
    enabled: editId !== null,
  });

  const openCreate = () => {
    setEditId(null);
    setDialogOpen(true);
  };
  const openEdit = useCallback((id: number) => {
    setEditId(id);
    setDialogOpen(true);
  }, []);
  useCreateIntent(() => openCreate());

  const columns = useMemo<GridColDef<GlossaryTerm>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'term',
        headerName: 'Term',
        flex: 1,
        minWidth: 200,
        renderCell: (p) =>
          canManage ? (
            <Box
              component="button"
              type="button"
              onClick={() => openEdit(p.row.id)}
              sx={{ background: 'none', border: 'none', p: 0, cursor: 'pointer', color: '#0a1a3f', fontWeight: 500, font: 'inherit', textAlign: 'left' }}
            >
              {p.row.term}
            </Box>
          ) : (
            p.row.term
          ),
      },
      {
        field: 'kind',
        headerName: 'Kind',
        width: 190,
        renderCell: (p) => (
          <Chip size="small" variant="outlined" label={KIND_LABEL[p.row.kind]} color={p.row.kind === 'keep' ? 'default' : 'primary'} />
        ),
      },
      {
        field: 'translations',
        headerName: 'Preferred translations',
        flex: 1.4,
        minWidth: 240,
        sortable: false,
        valueGetter: (_, row) => translationsSummary(row),
      },
      {
        field: 'source_lang',
        headerName: 'Source',
        width: 90,
        valueGetter: (_, row) => row.source_lang || 'any',
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        valueGetter: (_, row) => row.tenant.name,
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'enabled',
        headerName: 'Enabled',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => (p.value ? <CheckIcon fontSize="small" color="success" aria-label="yes" /> : null),
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 140,
        renderCell: (p) => <span title={p.row.updated_ts}>{formatRelativeTime(p.row.updated_ts)}</span>,
      },
    ],
    [canManage, openEdit],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      glossaryApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'term',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'scope',
      priority: 5,
      active: tenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker multiple value={tenants} onChange={setTenants} label="Operator" />
        </Box>
      ),
    },
    {
      key: 'kind',
      priority: 4,
      active: kindFilter !== 'any',
      node: (
        <TextField
          select
          size="small"
          label="Kind"
          value={kindFilter}
          sx={{ width: 200 }}
          onChange={(e) => setKindFilter(e.target.value as 'any' | GlossaryKind)}
        >
          <MenuItem value="any">Any</MenuItem>
          <MenuItem value="keep">Keep verbatim</MenuItem>
          <MenuItem value="translate">Preferred translations</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="translation-glossary"
        title="Translation Glossary"
        subtitle="Terms every machine translation of an operator keeps verbatim or translates as the operator prefers. The operator's own name is always kept."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New glossary term
            </Button>
          )
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<GlossaryTerm>
        queryKey={['translation_glossary']}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        defaultSort={{ field: 'term', sort: 'asc' }}
        emptyMessage="No glossary terms yet."
        headerToolbar={{
          onResetAllFilters: resetAllFilters,
          resetDisabled: !anyFilterActive,
          search: true,
          searchPlaceholder: 'Term or notes…',
          exportFileName: 'translation-glossary',
        }}
      />

      {dialogOpen && (editId === null || editQuery.data) && (
        <GlossaryTermDialog
          open={dialogOpen}
          term={editId === null ? null : (editQuery.data ?? null)}
          pageTenants={pageTenantsFrom(tenantIds, tenants)}
          onClose={() => {
            setDialogOpen(false);
            setEditId(null);
          }}
        />
      )}
    </Box>
  );
}
